import * as THREE from 'three';
import { hexDist, hexRing, hexToWorld, type BattleState, type MapDef } from '@somnhold/sim';
import { HIP_X, HIP_Y, POLE_LANTERN, VILLAGER_KINDS, villagerBody, villagerLeg } from '../models/villagers.ts';
import { Kit, place } from '../seasons/kit.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { OmenCtx } from '../omens/common.ts';

/**
 * The night of lanterns (Caras prestadas 2, docs/SAGAS.md): the people of Musgoviejo cross the plaza one after
 * another under strings of paper lanterns, and every hero carries a lantern. Inside a hero's light a real villager
 * throws a long shadow on the ground, away from the lantern; an impostor throws none (and shivers faintly, as if
 * the light went through it). A hero who stands by someone fills a ring; when it is an impostor its borrowed face
 * peels off like wet paper and a prestado is there.
 *
 * Draw calls: one instanced body mesh per villager kind (six), one for every leg, one for the long shadows, one for
 * the rings, one for the paper strips, one glow layer, the plaza's two merged meshes and Lis's name. No lights.
 */

const K = WORLD_PER_HEX / 1000;
const CAP = 24;
/** Lantern pools (a hero each, up to 8) and impostor rims. */
const POOLS = 8 + CAP;
const POOL_WARM = new THREE.Color('#ffcf9e');
const RIM_COLD = new THREE.Color('#8fb0ff');
/** People are drawn this much bigger than a hero's scale, to read in a crowd on a phone. */
const BIG = 1.25;
const LAMP = new THREE.Color('#ffd9b0');
const LAMP_HOT = new THREE.Color('#fff1dc');
const SHIVER = new THREE.Color('#b8c6ff');
const PAPER_RED = new THREE.Color('#ff6a52');
const PAPER_WARM = new THREE.Color('#ffd9b8');
const TINTS = ['#ffffff', '#e8ecf4', '#f4e8e8', '#e6eee6', '#eceaf2'];

const RING_VERT = /* glsl */ `
attribute vec4 iRing; // x, y, z, fill
attribute float iKind; // 0 counting, 1 flash
varying vec2 vUv;
varying float vFill;
varying float vKind;
void main() {
  vUv = uv;
  vFill = iRing.w;
  vKind = iKind;
  vec3 p = iRing.xyz + vec3(position.x, 0.0, -position.y) * 0.95;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}`;

const RING_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
varying float vFill;
varying float vKind;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float band = smoothstep(0.78, 0.82, r) * (1.0 - smoothstep(0.93, 0.97, r));
  float ang = fract(atan(p.x, p.y) / 6.2831853 + 0.5);
  float filled = step(ang, vFill);
  vec3 c = mix(vec3(0.82, 0.86, 0.95), vec3(1.0, 0.55, 0.45), filled);
  float a = band * mix(0.22, 0.95, filled);
  if (a < 0.01) discard;
  gl_FragColor = vec4(c, a);
  #include <colorspace_fragment>
}`;

/*
 * Ground discs, additive: kind 0 is a hero's lantern pool (bright, a crisp soft edge, so the lit ground reads as a
 * place); kind 1 the cold rim at an impostor's feet, where its shadow should be and isn't.
 */
const POOL_VERT = /* glsl */ `
attribute vec4 iPool; // x, y, z, radius
attribute vec4 iTint; // r, g, b, kind
varying vec2 vUv;
varying vec4 vTint;
void main() {
  vUv = uv;
  vTint = iTint;
  vec3 p = iPool.xyz + vec3(position.x, 0.0, -position.y) * iPool.w * 2.0;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}`;

const POOL_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
varying vec4 vTint;
void main() {
  float r = length(vUv * 2.0 - 1.0);
  float a;
  if (vTint.w < 0.5) {
    // The pool: even light inside, a firm edge, a thin brighter lip where it ends.
    a = (1.0 - smoothstep(0.86, 0.98, r)) * 0.30 + smoothstep(0.80, 0.9, r) * (1.0 - smoothstep(0.92, 0.98, r)) * 0.22;
  } else {
    // The impostor's rim: a cold ring that breathes.
    a = smoothstep(0.5, 0.68, r) * (1.0 - smoothstep(0.8, 0.98, r)) * (0.85 + 0.35 * sin(uTime * 6.0)) + (1.0 - smoothstep(0.0, 0.6, r)) * 0.12;
  }
  if (a < 0.005) discard;
  gl_FragColor = vec4(vTint.rgb * a, a);
  #include <colorspace_fragment>
}`;

/** A long soft shadow lying on the ground from the feet (origin) along +Z: dark at the feet, fading at the tip. */
const shadowTexture = (): THREE.Texture => {
  const c = document.createElement('canvas');
  c.width = 32;
  c.height = 128;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  for (let y = 0; y < 128; y++) {
    const k = y / 127;
    // Narrow at the feet, a body's width in the middle, a head at the tip.
    const w = 0.35 + 0.5 * Math.sin(Math.min(1, k * 1.25) * Math.PI * 0.5) - (k > 0.82 ? (k - 0.82) * 1.6 : 0);
    const a = 1 - k * 0.35;
    const gr = g.createLinearGradient(0, 0, 32, 0);
    gr.addColorStop(0, 'rgba(0,0,0,0)');
    gr.addColorStop(0.5 - w * 0.5, 'rgba(0,0,0,0)');
    gr.addColorStop(0.5 - w * 0.44, `rgba(0,0,0,${a})`);
    gr.addColorStop(0.5 + w * 0.44, `rgba(0,0,0,${a})`);
    gr.addColorStop(0.5 + w * 0.5, 'rgba(0,0,0,0)');
    gr.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = gr;
    g.fillRect(0, y, 32, 1);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

/** «Lis» over her head: a small label in the style of the hero tags. */
export const nameSprite = (text: string): THREE.Sprite => {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 48;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  g.fillStyle = 'rgba(16,17,20,0.82)';
  g.strokeStyle = 'rgba(236,238,242,0.55)';
  g.lineWidth = 2;
  g.beginPath();
  g.roundRect(14, 6, 100, 36, 6);
  g.fill();
  g.stroke();
  g.fillStyle = '#eceef2';
  g.font = '600 24px "Alegreya Sans", system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, 64, 25);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t, transparent: true, depthTest: false }));
  sp.scale.set(0.62, 0.23, 1);
  sp.renderOrder = 20;
  return sp;
};

interface Track {
  px: number;
  py: number;
  cx: number;
  cy: number;
  dist: number;
  near: number;
}

interface Paper {
  p: THREE.Vector3;
  v: THREE.Vector3;
  r: THREE.Euler;
  w: THREE.Vector3;
  life: number;
  max: number;
  red: boolean;
}

export class CrowdLayer {
  readonly group = new THREE.Group();
  private bodies: THREE.InstancedMesh[] = [];
  private legs: THREE.InstancedMesh;
  private shadows: THREE.InstancedMesh;
  private rings: THREE.InstancedBufferGeometry;
  private ringPos: Float32Array;
  private ringKind: Float32Array;
  private ringMat: THREE.ShaderMaterial;
  private papers: THREE.InstancedMesh;
  private paper: Paper[] = [];
  private glow: GlowLayer;
  private pools: THREE.InstancedBufferGeometry;
  private poolPos: Float32Array;
  private poolTint: Float32Array;
  private poolMat: THREE.ShaderMaterial;
  private lis: THREE.Sprite;
  private plaza: THREE.Object3D[] = [];
  private plazaGlows: { p: THREE.Vector3; c: THREE.Color; ph: number }[] = [];
  private track = new Map<number, Track>();
  private flashes: { x: number; y: number; z: number; t: number }[] = [];
  private mat: THREE.MeshStandardMaterial;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e = new THREE.Euler();
  private col = new THREE.Color();
  private now = 0;
  private last = 0;

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    route: { x: number; y: number }[],
    private lantern: number,
    private holdTicks: number,
  ) {
    const low = ctx.low;
    this.group.name = 'crowd';
    // A faint warm lift: people read apart from the nightmares in the dark.
    this.mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true, emissive: '#3a2a1e', emissiveIntensity: 1 });
    for (let k = 0; k < VILLAGER_KINDS; k++) {
      const im = new THREE.InstancedMesh(villagerBody(k, k * 3 + 1), this.mat, CAP);
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      im.count = 0;
      im.frustumCulled = false;
      this.bodies.push(im);
      this.group.add(im);
    }
    this.legs = new THREE.InstancedMesh(villagerLeg(), this.mat, CAP * 2);
    this.legs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.legs.count = 0;
    this.legs.frustumCulled = false;
    this.group.add(this.legs);
    // The lantern pools and the impostors' rims, on the ground under the shadows.
    this.pools = new THREE.InstancedBufferGeometry();
    const pq = new THREE.PlaneGeometry(1, 1);
    this.pools.index = pq.index;
    this.pools.setAttribute('position', pq.getAttribute('position'));
    this.pools.setAttribute('uv', pq.getAttribute('uv'));
    this.poolPos = new Float32Array(POOLS * 4);
    this.poolTint = new Float32Array(POOLS * 4);
    this.pools.setAttribute('iPool', new THREE.InstancedBufferAttribute(this.poolPos, 4).setUsage(THREE.DynamicDrawUsage));
    this.pools.setAttribute('iTint', new THREE.InstancedBufferAttribute(this.poolTint, 4).setUsage(THREE.DynamicDrawUsage));
    this.pools.instanceCount = 0;
    this.poolMat = new THREE.ShaderMaterial({ vertexShader: POOL_VERT, fragmentShader: POOL_FRAG, uniforms: { uTime: { value: 0 } }, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    const pm = new THREE.Mesh(this.pools, this.poolMat);
    pm.frustumCulled = false;
    pm.renderOrder = 10;
    this.group.add(pm);
    // Long shadows on the ground.
    const sg = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(0, 0, 0.5);
    this.shadows = new THREE.InstancedMesh(sg, new THREE.MeshBasicMaterial({ map: shadowTexture(), transparent: true, depthWrite: false, color: '#05060a' }), CAP);
    this.shadows.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.shadows.count = 0;
    this.shadows.frustumCulled = false;
    // Over the lanterns' warm pools (additive, drawn at 10): the shadow darkens the lit ground.
    this.shadows.renderOrder = 11;
    this.group.add(this.shadows);
    // The rings that fill while a hero stands by someone.
    this.rings = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1);
    this.rings.index = quad.index;
    this.rings.setAttribute('position', quad.getAttribute('position'));
    this.rings.setAttribute('uv', quad.getAttribute('uv'));
    this.ringPos = new Float32Array(CAP * 4);
    this.ringKind = new Float32Array(CAP);
    this.rings.setAttribute('iRing', new THREE.InstancedBufferAttribute(this.ringPos, 4).setUsage(THREE.DynamicDrawUsage));
    this.rings.setAttribute('iKind', new THREE.InstancedBufferAttribute(this.ringKind, 1).setUsage(THREE.DynamicDrawUsage));
    this.rings.instanceCount = 0;
    this.ringMat = new THREE.ShaderMaterial({ vertexShader: RING_VERT, fragmentShader: RING_FRAG, uniforms: { uTime: { value: 0 } }, transparent: true, depthWrite: false });
    const rm = new THREE.Mesh(this.rings, this.ringMat);
    rm.frustumCulled = false;
    rm.renderOrder = 6;
    this.group.add(rm);
    // Strips of the borrowed face, flying off when it peels.
    const pg = new THREE.PlaneGeometry(0.09, 0.2);
    this.papers = new THREE.InstancedMesh(pg, new THREE.MeshBasicMaterial({ color: '#ffffff', side: THREE.DoubleSide }), 96);
    this.papers.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.papers.count = 0;
    this.papers.frustumCulled = false;
    this.group.add(this.papers);
    this.glow = new GlowLayer(low ? 160 : 320);
    this.group.add(this.glow.mesh);
    this.lis = nameSprite('Lis');
    this.lis.visible = false;
    this.group.add(this.lis);
    this.buildPlaza(map, route, low);
  }

  /** Strings of paper lanterns across the plaza, high between posts, clear of the roads. */
  private buildPlaza(map: MapDef, route: { x: number; y: number }[], low: boolean): void {
    const wood = new Kit();
    const shade = new Kit();
    const road = map.sectors.flatMap((s) => s.path);
    const posts: THREE.Vector3[] = [];
    // Posts round the plaza's edge (three hexes out), spaced apart and off every road.
    const ring = hexRing(3).filter((h) => !road.some((x) => hexDist(x, h) <= 0));
    const picked: typeof ring = [];
    for (let i = 0; i < ring.length && route.length; i++) {
      const h = ring[(i * 5) % ring.length] as (typeof ring)[number];
      if (picked.includes(h) || picked.some((o) => hexDist(o, h) < 2)) continue;
      picked.push(h);
      if (picked.length >= 7) break;
    }
    // Round the ring in order, so the strings run along its edge.
    picked.sort((a, b) => {
      const wa = hexToWorld(a.q, a.r), wb = hexToWorld(b.q, b.r);
      return Math.atan2(wa.y, wa.x) - Math.atan2(wb.y, wb.x);
    });
    for (const h of picked) {
      const w = hexToWorld(h.q, h.r);
      // Half a hex outward from the hex's middle: at the plaza's rim, not in the fight.
      const d = Math.hypot(w.x, w.y) || 1;
      posts.push(this.ctx.at(w.x + (w.x / d) * 450, w.y + (w.y / d) * 450, 0));
    }
    for (const p of posts) {
      wood.add(new THREE.CylinderGeometry(0.035, 0.05, 2.6, 6).translate(0, 1.3, 0), '#3a2c22', place(p.x, p.y, p.z));
      wood.add(new THREE.BoxGeometry(0.3, 0.05, 0.05).translate(0, 2.55, 0), '#3a2c22', place(p.x, p.y, p.z, Math.atan2(p.x, p.z)));
    }
    // Strings: around the ring and two across the plaza, sagging, a paper lantern every half unit.
    const pairs: [THREE.Vector3, THREE.Vector3][] = [];
    for (let i = 0; i < posts.length; i++) pairs.push([posts[i] as THREE.Vector3, posts[(i + 1) % posts.length] as THREE.Vector3]);
    const rope = new THREE.CylinderGeometry(0.008, 0.008, 1, 4);
    const lan = new THREE.SphereGeometry(0.085, 8, 6).scale(1, 1.25, 1);
    const cap = new THREE.CylinderGeometry(0.04, 0.05, 0.03, 6);
    let li = 0;
    for (const [a, b] of pairs) {
      const A = a.clone().setY(a.y + 2.5);
      const B = b.clone().setY(b.y + 2.5);
      const len = A.distanceTo(B);
      const steps = Math.max(3, Math.round(len / 0.8));
      const sag = Math.min(0.5, len * 0.08);
      let prev = A.clone();
      for (let s = 1; s <= steps; s++) {
        const k = s / steps;
        const p = A.clone().lerp(B, k);
        p.y -= sag * 4 * k * (1 - k);
        const mid = prev.clone().add(p).multiplyScalar(0.5);
        const d = p.clone().sub(prev);
        const m = new THREE.Matrix4().compose(mid, new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().normalize()), new THREE.Vector3(1, d.length(), 1));
        wood.add(rope, '#2a2420', m);
        if (s < steps) {
          const red = (li++ + s) % 3 === 0;
          const at = p.clone().setY(p.y - 0.14);
          shade.add(lan, red ? '#ff6a52' : '#f2eee8', place(at.x, at.y, at.z));
          wood.add(cap, '#2a2420', place(at.x, at.y + 0.11, at.z));
          if (!low || s % 2 === 0) this.plazaGlows.push({ p: at, c: red ? PAPER_RED : PAPER_WARM, ph: li * 1.7 });
        }
        prev = p;
      }
    }
    const wm = wood.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9 }), 'plaza-posts');
    const sm = shade.build(new THREE.MeshBasicMaterial({ vertexColors: true }), 'plaza-lanterns');
    for (const m of [wm, sm]) if (m) {
      this.group.add(m);
      this.plaza.push(m);
    }
  }

  /** Every fresh sim tick: where each walker was and is (for the stride between ticks), and who is being filled. */
  tick(s: BattleState): void {
    const seen = new Set<number>();
    for (const w of s.crowd?.people ?? []) {
      seen.add(w.id);
      const t = this.track.get(w.id);
      if (!t) this.track.set(w.id, { px: w.x, py: w.y, cx: w.x, cy: w.y, dist: 0, near: w.near });
      else {
        t.dist += Math.hypot(w.x - t.cx, w.y - t.cy) * K;
        t.px = t.cx;
        t.py = t.cy;
        t.cx = w.x;
        t.cy = w.y;
        // Someone real, stood by all the way: a grey flash says «not this one».
        if (!w.face && w.near >= this.holdTicks && t.near < this.holdTicks) {
          const p = this.ctx.at(w.x, w.y, 0.05);
          this.flashes.push({ x: p.x, y: p.y, z: p.z, t: 0 });
          this.ctx.vfx.ring(p, 0.2, 0.9, '#c4c8d2', 10, 0.6);
        }
        t.near = w.near;
      }
    }
    for (const id of [...this.track.keys()]) if (!seen.has(id)) this.track.delete(id);
  }

  /** An impostor unmasked at (x, y): its face peels off like wet paper, a pale flash, the crowd's light shivers. */
  unmask(x: number, y: number): void {
    const p = this.ctx.at(x, y, 1.15 * SCALE.hero * 1.0);
    this.ctx.flare(p, '#e8eeff', 3, 12);
    this.ctx.vfx.ring(this.ctx.at(x, y, 0.06), 0.2, 1.6, '#e8ecf4', 12, 0.7);
    this.ctx.shake(0.15);
    const n = this.ctx.low ? 14 : 26;
    for (let i = 0; i < n && this.paper.length < 96; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 0.02 + Math.random() * 0.035;
      this.paper.push({
        p: p.clone().add(new THREE.Vector3((Math.random() - 0.5) * 0.15, (Math.random() - 0.3) * 0.2, (Math.random() - 0.5) * 0.15)),
        v: new THREE.Vector3(Math.cos(a) * sp, 0.02 + Math.random() * 0.04, Math.sin(a) * sp),
        r: new THREE.Euler(Math.random() * 3, Math.random() * 3, Math.random() * 3),
        w: new THREE.Vector3((Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3),
        life: 0,
        max: 40 + Math.random() * 30,
        red: i % 7 === 0,
      });
    }
  }

  /** Every redraw: the crowd where it is between ticks, its shadows under the heroes' lanterns, the rings and the glows. */
  draw(s: BattleState, alpha: number, contact?: (x: number, y: number, z: number, size: number) => void): void {
    const cr = s.crowd;
    const rules = s.rules?.crowd;
    const counts = new Array<number>(VILLAGER_KINDS).fill(0);
    let legs = 0;
    let shadows = 0;
    let rings = 0;
    let pools = 0;
    const pool = (x: number, y: number, z: number, r: number, c: THREE.Color, kind: number): void => {
      if (pools >= POOLS) return;
      this.poolPos.set([x, y, z, r], pools * 4);
      this.poolTint.set([c.r, c.g, c.b, kind], pools * 4);
      pools++;
    };
    this.glow.begin();
    // Every standing hero's lantern: where it is, its pool on the ground and the flame at their side.
    const lights: THREE.Vector3[] = [];
    const R = this.lantern * K;
    for (const h of s.heroes) {
      if (h.downTicks > 0) continue;
      const p = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
      lights.push(p);
      const f = 0.94 + 0.06 * Math.sin(this.now * 7 + h.id);
      pool(p.x, p.y + 0.03, p.z, R, POOL_WARM, 0);
      this.glow.add(p.x, p.y + 0.08, p.z, LAMP, R * 1.2, 0.16 * f);
      this.glow.add(p.x + 0.16, p.y + 0.62, p.z, LAMP_HOT, 0.55, 1.0 * f);
    }
    let lisShown = false;
    if (cr && rules) {
      for (const w of cr.people) {
        const t = this.track.get(w.id);
        const sx = t ? t.px + (t.cx - t.px) * alpha : w.x;
        const sy = t ? t.py + (t.cy - t.py) * alpha : w.y;
        const ground = this.ctx.at(sx, sy, 0);
        const kind = w.lis ? 1 : w.id % VILLAGER_KINDS;
        const kid = kind === 3;
        const sc = SCALE.hero * BIG * (kid ? 0.68 : 1) * (0.96 + ((w.id * 37) % 9) * 0.01);
        const yaw = facingToRotY(w.facing);
        const stride = (t?.dist ?? 0) * 5.2 / (kid ? 0.75 : 1);
        const bob = Math.abs(Math.sin(stride)) * 0.03;
        contact?.(ground.x, ground.y, ground.z, kid ? 0.42 : 0.55);
        // The body.
        const bi = counts[kind] as number;
        if (bi < CAP) {
          this.q.setFromAxisAngle(this.v.set(0, 1, 0), yaw + Math.sin(stride) * 0.04);
          this.m.compose(this.v.set(ground.x, ground.y + bob, ground.z), this.q, this.s3.set(sc, sc, sc));
          const body = this.bodies[kind] as THREE.InstancedMesh;
          body.setMatrixAt(bi, this.m);
          body.setColorAt(bi, this.col.set(TINTS[w.id % TINTS.length] as string));
          counts[kind] = bi + 1;
          // Two legs swung about the hips.
          for (const sd of [-1, 1]) {
            if (legs >= CAP * 2) break;
            const swing = Math.sin(stride) * 0.55 * sd;
            this.e.set(swing, yaw, 0, 'YXZ');
            this.q.setFromEuler(this.e);
            const hx = sd * HIP_X * sc;
            const hip = this.v.set(ground.x + Math.cos(yaw) * hx, ground.y + HIP_Y * sc + bob, ground.z - Math.sin(yaw) * hx);
            this.m.compose(hip, this.q, this.s3.set(sc, sc, sc));
            this.legs.setMatrixAt(legs, this.m);
            this.legs.setColorAt(legs, this.col.set('#ffffff'));
            legs++;
          }
        }
        // Who is lighting them: the nearest standing hero inside their lantern's reach.
        let best: THREE.Vector3 | null = null;
        let bd = R;
        for (const l of lights) {
          const d = Math.hypot(l.x - ground.x, l.z - ground.z);
          if (d < bd) {
            bd = d;
            best = l;
          }
        }
        if (best) {
          if (!w.face) {
            // A real one: the shadow, long and dark, away from the light (longer the farther from it).
            if (shadows < CAP) {
              const away = Math.atan2(ground.x - best.x, ground.z - best.z);
              const len = (1.5 + (bd / R) * 1.3) * sc * 2.3;
              const fade = 1 - Math.max(0, (bd / R - 0.85) / 0.15);
              this.q.setFromAxisAngle(this.v.set(0, 1, 0), away);
              this.m.compose(this.v.set(ground.x, ground.y + 0.03, ground.z), this.q, this.s3.set(0.95 * sc * 1.8 * fade, 1, len * fade));
              this.shadows.setMatrixAt(shadows++, this.m);
            }
          } else {
            // An impostor: no shadow at all. A cold rim breathes at its feet where the shadow should lie, a faint
            // shiver hangs about it, and every second and a half the light goes right through it.
            const sh = 0.5 + 0.5 * Math.sin(this.now * 9 + w.id * 3);
            pool(ground.x, ground.y + 0.035, ground.z, 0.62 * sc * 1.8, RIM_COLD, 1);
            this.glow.add(ground.x, ground.y + 0.55 * sc * 1.8, ground.z, SHIVER, 0.9 * sc * 1.8, 0.07 + 0.06 * sh);
            const ph = (this.now + w.id * 0.37) % 1.5;
            if (ph < 0.22) this.glow.add(ground.x, ground.y + 0.6 * sc * 1.8, ground.z, SHIVER, 1.25 * sc * 1.8, 0.5 * (1 - ph / 0.22));
          }
        }
        // A lantern on a pole lights its bearer.
        if (kind === 4) {
          const c = Math.cos(yaw), sn = Math.sin(yaw);
          const lx = POLE_LANTERN.x * c + POLE_LANTERN.z * sn;
          const lz = -POLE_LANTERN.x * sn + POLE_LANTERN.z * c;
          this.glow.add(ground.x + lx * sc, ground.y + POLE_LANTERN.y * sc + bob, ground.z + lz * sc, PAPER_WARM, 0.3, 0.55);
        }
        // A hero standing by them: the ring fills.
        if (w.near > 0 && rings < CAP) {
          this.ringPos.set([ground.x, ground.y + 0.04, ground.z, Math.min(1, w.near / this.holdTicks)], rings * 4);
          this.ringKind[rings++] = 0;
        }
        if (w.lis && !lisShown) {
          this.lis.position.set(ground.x, ground.y + 1.95 * sc * 1.0 + 0.35, ground.z);
          lisShown = true;
        }
      }
    }
    this.lis.visible = lisShown;
    for (let k = 0; k < VILLAGER_KINDS; k++) {
      const b = this.bodies[k] as THREE.InstancedMesh;
      b.count = counts[k] as number;
      b.instanceMatrix.needsUpdate = true;
      if (b.instanceColor) b.instanceColor.needsUpdate = true;
    }
    this.legs.count = legs;
    this.legs.instanceMatrix.needsUpdate = true;
    if (this.legs.instanceColor) this.legs.instanceColor.needsUpdate = true;
    this.pools.instanceCount = pools;
    (this.pools.getAttribute('iPool') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (this.pools.getAttribute('iTint') as THREE.InstancedBufferAttribute).needsUpdate = true;
    this.shadows.count = shadows;
    this.shadows.instanceMatrix.needsUpdate = true;
    this.rings.instanceCount = rings;
    (this.rings.getAttribute('iRing') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (this.rings.getAttribute('iKind') as THREE.InstancedBufferAttribute).needsUpdate = true;
    // The plaza's paper lanterns, breathing.
    for (const g of this.plazaGlows) this.glow.add(g.p.x, g.p.y, g.p.z, g.c, 0.3, 0.3 + 0.06 * Math.sin(this.now * 2.2 + g.ph));
    this.glow.end();
  }

  /** Every frame: time, and the paper strips tumbling down. */
  frame(now: number): void {
    const dt = this.last ? Math.min(0.1, now - this.last) : 0;
    this.last = now;
    this.now = now;
    this.ringMat.uniforms.uTime!.value = now;
    this.poolMat.uniforms.uTime!.value = now;
    const steps = dt * 60;
    let n = 0;
    for (const p of this.paper) {
      p.life += steps;
      p.v.y -= 0.0012 * steps;
      p.v.multiplyScalar(Math.pow(0.97, steps));
      p.p.addScaledVector(p.v, steps);
      const floor = this.ctx.ground(p.p.x, p.p.z) + 0.02;
      if (p.p.y < floor) {
        p.p.y = floor;
        p.v.set(0, 0, 0);
      } else {
        p.r.x += p.w.x * steps;
        p.r.y += p.w.y * steps;
        p.r.z += p.w.z * steps;
      }
    }
    this.paper = this.paper.filter((p) => p.life < p.max);
    for (const p of this.paper) {
      const k = 1 - Math.max(0, (p.life - p.max * 0.6) / (p.max * 0.4));
      this.q.setFromEuler(p.r);
      this.m.compose(p.p, this.q, this.s3.set(k, k, k));
      this.papers.setMatrixAt(n, this.m);
      this.papers.setColorAt(n, this.col.set(p.red ? '#d9a39a' : '#eef0f3'));
      n++;
    }
    this.papers.count = n;
    this.papers.instanceMatrix.needsUpdate = true;
    if (this.papers.instanceColor) this.papers.instanceColor.needsUpdate = true;
    this.flashes = this.flashes.filter((f) => ++f.t < 20);
  }

  dispose(): void {
    for (const b of this.bodies) b.geometry.dispose();
    this.legs.geometry.dispose();
    this.shadows.geometry.dispose();
    this.rings.dispose();
    this.pools.dispose();
    this.papers.geometry.dispose();
    for (const p of this.plaza) (p as THREE.Mesh).geometry.dispose();
    this.glow.mesh.geometry.dispose();
    this.lis.material.map?.dispose();
    this.group.removeFromParent();
  }
}
